import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Divider,
  Chip,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import {
  TrendingUp,
  Settings,
  Save,
  Refresh,
  LocationOn
} from '@mui/icons-material';
import { API_URL } from './config';

const DetourDistanceControl = () => {
  const [settings, setSettings] = useState({
    // Ride-matching detour distance settings
    default_detour_distance: 3.0, // Default detour distance in kilometers
    min_detour_distance: 0.5, // Minimum detour distance in kilometers
    max_detour_distance: 10.0, // Maximum detour distance in kilometers
    detour_increment: 0.5, // Increment step for detour distance adjustment
    auto_detour_expansion: false, // Auto-expand detour distance if no matches found
    expansion_time_limit: 300, // Time limit before auto-expansion (seconds)
    max_auto_expansions: 3 // Maximum number of auto-expansions
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  useEffect(() => {
    fetchDetourSettings();
  }, []);

  const fetchDetourSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch detour distance settings
      const detourResponse = await fetch(`${API_URL}/ride-matching/detour-settings`);
      const detourData = await detourResponse.json();
      
      if (detourData.success && detourData.data) {
        setSettings(prev => ({ ...prev, ...detourData.data }));
      }
    } catch (error) {
      console.error('Error fetching detour settings:', error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Failed to fetch detour settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateSettings = () => {
    if (!settings.default_detour_distance || settings.default_detour_distance <= 0) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Default detour distance must be greater than 0'
      });
      return false;
    }

    if (settings.min_detour_distance >= settings.max_detour_distance) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Minimum detour distance must be less than maximum'
      });
      return false;
    }

    if (settings.default_detour_distance < settings.min_detour_distance || 
        settings.default_detour_distance > settings.max_detour_distance) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Default detour distance must be between minimum and maximum'
      });
      return false;
    }

    return true;
  };

  const handleSaveSettings = async () => {
    if (!validateSettings()) return;

    try {
      setSaving(true);
      
      // Extract detour distance settings
      const detourSettings = {
        default_detour_distance: settings.default_detour_distance,
        min_detour_distance: settings.min_detour_distance,
        max_detour_distance: settings.max_detour_distance,
        detour_increment: settings.detour_increment,
        auto_detour_expansion: settings.auto_detour_expansion,
        expansion_time_limit: settings.expansion_time_limit,
        max_auto_expansions: settings.max_auto_expansions
      };
      
      // Save detour distance settings
      const detourResponse = await fetch(`${API_URL}/ride-matching/detour-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(detourSettings),
      });

      const detourData = await detourResponse.json();
      
      if (detourData.success) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Detour distance settings updated successfully'
        });
      } else {
        throw new Error('Failed to update detour settings');
      }
    } catch (error) {
      console.error('Error saving detour settings:', error);
      setAlert({
        show: true,
        type: 'error',
        message: 'Failed to update detour settings'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Loading detour distance settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocationOn sx={{ mr: 2, color: '#28a745' }} />
          <Typography variant="h4" fontWeight="600">
            Ride-Matching Detour Distance Control
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchDetourSettings}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Alert */}
      {alert.show && (
        <Alert 
          severity={alert.type} 
          sx={{ mb: 3 }}
          onClose={() => setAlert({ ...alert, show: false })}
        >
          {alert.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Detour Distance Control */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ mr: 1, color: '#28a745' }} />
                <Typography variant="h6" fontWeight="600">
                  Detour Distance Settings
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Default Detour Distance (km)"
                  type="number"
                  value={settings.default_detour_distance}
                  onChange={(e) => handleSettingChange('default_detour_distance', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: settings.min_detour_distance, max: settings.max_detour_distance, step: settings.detour_increment }}
                  helperText={`Maximum detour distance for passenger matching (${settings.min_detour_distance}km - ${settings.max_detour_distance}km)`}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Minimum Detour Distance (km)"
                  type="number"
                  value={settings.min_detour_distance}
                  onChange={(e) => handleSettingChange('min_detour_distance', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0.1, max: 2, step: 0.1 }}
                  helperText="Minimum allowed detour distance for passengers"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Maximum Detour Distance (km)"
                  type="number"
                  value={settings.max_detour_distance}
                  onChange={(e) => handleSettingChange('max_detour_distance', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 5, max: 25, step: 1 }}
                  helperText="Maximum allowed detour distance for passengers"
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Detour Increment (km)"
                  type="number"
                  value={settings.detour_increment}
                  onChange={(e) => handleSettingChange('detour_increment', parseFloat(e.target.value))}
                  fullWidth
                  inputProps={{ min: 0.1, max: 2, step: 0.1 }}
                  helperText="Step size for detour distance adjustments"
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.auto_detour_expansion}
                      onChange={(e) => handleSettingChange('auto_detour_expansion', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Auto-Expand Detour Distance if No Matches"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Automatically increase detour distance if no passengers found within time limit
                </Typography>
              </Box>

              {settings.auto_detour_expansion && (
                <>
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Expansion Time Limit (seconds)"
                      type="number"
                      value={settings.expansion_time_limit}
                      onChange={(e) => handleSettingChange('expansion_time_limit', parseInt(e.target.value))}
                      fullWidth
                      inputProps={{ min: 60, max: 1800, step: 30 }}
                      helperText="Time to wait before expanding detour distance (1-30 minutes)"
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <TextField
                      label="Max Auto-Expansions"
                      type="number"
                      value={settings.max_auto_expansions}
                      onChange={(e) => handleSettingChange('max_auto_expansions', parseInt(e.target.value))}
                      fullWidth
                      inputProps={{ min: 1, max: 10, step: 1 }}
                      helperText="Maximum number of automatic detour distance expansions"
                    />
                  </Box>
                </>
              )}

              <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e8', borderRadius: 1 }}>
                <Typography variant="body2" color="#2e7d32" fontWeight="500">
                  💡 Tip: Larger detour distance improves matching chances but increases driver route deviation
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ mr: 1, color: '#007bff' }} />
                <Typography variant="h6" fontWeight="600">
                  Current Settings Status
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Current Configuration:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Chip 
                    label={`Default: ${settings.default_detour_distance}km`} 
                    variant="outlined" 
                    size="small" 
                    color="primary" 
                  />
                  <Chip 
                    label={`Range: ${settings.min_detour_distance}km - ${settings.max_detour_distance}km`} 
                    variant="outlined" 
                    size="small" 
                    color="info" 
                  />
                  <Chip 
                    label={`Auto-Expansion: ${settings.auto_detour_expansion ? 'Enabled' : 'Disabled'}`} 
                    variant="outlined" 
                    size="small" 
                    color={settings.auto_detour_expansion ? 'success' : 'default'} 
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Impact on System:
                </Typography>
                <Typography variant="body2">
                  • <strong>Matching Rate:</strong> {settings.default_detour_distance >= 3 ? 'High' : 'Medium'}
                  <br />
                  • <strong>Driver Efficiency:</strong> {settings.default_detour_distance <= 5 ? 'High' : 'Medium'}
                  <br />
                  • <strong>Passenger Access:</strong> {settings.max_detour_distance >= 10 ? 'Excellent' : 'Good'}
                </Typography>
              </Box>

              <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" color="#6c757d" fontWeight="500">
                  📊 System Performance
                </Typography>
                <Typography variant="body2" color="#6c757d" sx={{ mt: 1 }}>
                  Current settings are optimized for balanced matching between driver efficiency and passenger accessibility.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={fetchDetourSettings}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveSettings}
          disabled={saving}
          startIcon={<Save />}
          sx={{ minWidth: 120 }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default DetourDistanceControl;
